import { format } from 'date-fns';

interface MergedValues {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

type MergedValuesArg = MergedValues | null | undefined;

// taken from: https://stackoverflow.com/questions/51603250/typescript-3-parameter-list-intersection-type/51604379#51604379
type TupleTypes<T> = { [P in keyof T]: T[P] } extends { [key: number]: infer V }
  ? NullToObject<V>
  : never;
// eslint-disable-next-line @typescript-eslint/ban-types
type NullToObject<T> = T extends null | undefined ? {} : T;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;

/**
 * @link https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/utils/src/mergeProps.ts
 *
 *
 * Merges multiple values together. the last prop object overrides all previous ones.
 * @param args - Multiple sets of props to merge together.
 */
export function mergeValues<T extends MergedValuesArg[]>(
  ...args: T
): UnionToIntersection<TupleTypes<T>> {
  // Start with a base clone of the first argument. This is a lot faster than starting
  // with an empty object and adding properties as we go.
  // eslint-disable-next-line prefer-const
  let result: MergedValues = { ...args[0] };
  for (let i = 1; i < args.length; i++) {
    // eslint-disable-next-line prefer-const
    let props = args[i];
    // eslint-disable-next-line prefer-const
    for (let key in props) {
      const a = result[key];
      const b = props[key];
      result[key] = b ? b : a;
    }
  }

  return result as UnionToIntersection<TupleTypes<T>>;
}

export const formattedSubString = (str: string, length = 12) => {
  if (str.length <= length - 1) return str;
  return str.substring(0, length - 1) + '...';
};

export const toKebabCase = (str: string): string => {
  return str.toLowerCase().replace(/\s+/g, '-');
};

const defaultDateToken = 'MMM dd, yyyy';
export const formatDate = (date: Date, formatToken?: string) => {
  return format(date, formatToken ?? defaultDateToken);
};

const defaultTimeToken = 'hh:mm aa';
export const formatTime = (date: Date, formatToken?: string) => {
  return format(date, formatToken ?? defaultTimeToken);
};

export const formatDateTime = (date: Date, formatToken?: string) => {
  return format(date, formatToken ?? `${defaultDateToken} ${defaultTimeToken}`);
};

export const toPayloadTimeFormat = (
  date: Date,
  options?: { includeSeconds: boolean },
) => {
  return formatTime(date, options?.includeSeconds ? 'HH:mm:ss' : 'HH:mm');
};

export const isoTimezoneRangeForLocalDate = (localDate: Date) => {
  const startOfDayLocal = new Date(localDate);
  startOfDayLocal.setHours(0, 0, 0, 0);
  const endOfDayLocal = new Date(localDate);
  endOfDayLocal.setHours(23, 59, 59, 999);

  const startOfDayISO = startOfDayLocal.toISOString();
  const endOfDayISO = endOfDayLocal.toISOString();

  return { startOfDayISO, endOfDayISO };
};

export const toPayloadDateFormat = (date: Date) => {
  return formatDate(date, 'yyyy-MM-dd');
};

export const stripUndefined = (obj: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): { [key: string]: any } => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: { [key: string]: any } = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
};

export const stripNull = (obj: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): { [key: string]: any } => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: { [key: string]: any } = {};
  for (const key in obj) {
    if (obj[key] !== null) {
      result[key] = obj[key];
    }
  }
  return result;
};
